import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type ItemModel from 'bcl/models/item';
import type LocationItemModel from 'bcl/models/location-item';
import type SupplierItemModel from 'bcl/models/supplier-item';
import Wide from 'bcl/components/input/wide';
import Checkbox from 'bcl/components/input/checkbox';
import SelectProductgroup from 'bcl/components/input/select-productgroup';
import { Input } from '@ember/component';
import { concat, fn, get } from '@ember/helper';
import { on } from '@ember/modifier';
import gt from 'ember-truth-helpers/helpers/gt';
import displayDate from 'bcl/helpers/display-date';
import sort from 'bcl/helpers/sort';
import filterSort from 'bcl/helpers/filter-sort';
import plusOne from 'bcl/helpers/plus-one';
import SelectSuppliers from 'bcl/components/input/select-suppliers';
import type SupplierModel from 'bcl/models/supplier';
import InputWithFormatting from 'bcl/components/input/with-formatting';
import displaySize, { reverseDisplaySize } from 'bcl/helpers/display-size';
import identity from 'bcl/helpers/identity';
import euro, { reverseEuro } from 'bcl/helpers/euro';

interface ItemEditSignature {
  // The arguments accepted by the component
  Args: {
    model: ItemModel;
    closeModal: () => void;
  };
  // Any blocks yielded by the component
  Blocks: {
    default: [];
  };
  // The element to which `...attributes` is applied in the component template
  Element: null;
}

export default class ItemEditComponent extends Component<ItemEditSignature> {
  <template>
    <form class="mx-4 sm:mx-0">
      <div class="space-y-12">
        <div class="border-b border-gray-900/10 pb-12">
          <h2
            class="text-base font-semibold leading-7 text-gray-900"
          >Artikel-Stammdaten</h2>
          <p class="mt-1 text-sm leading-6 text-gray-600">Das sind
            Informationen, die sich normalerweise nicht ändern.</p>
          <div class="grid grid-cols-1 gap-x-6 sm:grid-cols-6">

            <Wide
              @label="Artikelname"
              @name="name"
              @type="text"
              @value={{@model.name}}
            />
            <Wide
              @label="(abweichender) kurzer&nbsp;Name"
              @name="short_name"
              @type="text"
              @value={{@model.short_name}}
            />
            <Checkbox @label="Aktiv" @name="active" @value={{@model.active}} />

            <SelectProductgroup
              @label="Warengruppe"
              @name="product_group"
              @value={{@model.product_group}}
              @onChange={{@model.updateProductGroup}}
            />
            <Wide
              @label="Soll-Bestand"
              @name="target_amount"
              @type="text"
              @value={{@model.virtual_target_amount}}
            />
            <div
              class="col-span-6 col-start-2 mb-2 -mt-1 text-gray-700 text-sm"
            >
              Wie viele Gebinde sollen auf Lager sein?
            </div>

          </div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Pfand und
            Preise</h2>
          {{! <p class="mt-1 text-sm leading-6 text-gray-600">Pfand und Preise</p> }}
          <div class="grid grid-cols-1 gap-x-6 sm:grid-cols-6">
            <Wide
              @label="Pfand Verpackung"
              @name="deposit_container"
              @type="text"
              @value={{euro @model.deposit_container}}
              @update={{fn (mut @model.deposit_container)}}
              @updateMut={{reverseEuro}}
            />
            <Wide
              @label="Pfand pro Flasche"
              @name="deposit_single"
              @type="text"
              @value={{euro @model.deposit_single}}
              @update={{fn (mut @model.deposit_single)}}
              @updateMut={{reverseEuro}}
            />
            <div class="col-span-6 col-start-2 my-1">
              Einkaufspreis siehe Abschnitt Lieferanten
            </div>
            <Wide
              @label="Vk-Brutto"
              @name="gross_price"
              @type="text"
              @value={{euro @model.gross_price}}
              @update={{fn (mut @model.gross_price)}}
              @updateMut={{reverseEuro}}
            />

          </div>
          <h2
            class="text-base font-semibold leading-7 text-gray-900"
          >Mengen</h2>
          {{! <p class="mt-1 text-sm leading-6 text-gray-600">Pfand und Preise</p> }}
          <div class="grid grid-cols-1 gap-x-6 sm:grid-cols-6">

            <div
              class="sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-2 col-span-6"
            >
              <label
                for="container_size"
                class="block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5 sm:text-right"
              >Gebinde</label>
              <div class="mt-2 sm:col-span-2 sm:mt-0">
                <InputWithFormatting
                  @type="text"
                  name="container_size"
                  id="container_size"
                  @value={{identity @model.container_size}}
                  @update={{this.updateContainerSize}}
                  class="inline w-16 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                />
                <span class="mx-1">
                  x
                </span>
                <InputWithFormatting
                  @type="text"
                  @value={{displaySize @model.single_size}}
                  @update={{fn (mut @model.single_size)}}
                  @updateMut={{reverseDisplaySize}}
                  name="single_size"
                  id="single_size"
                  class="inline w-20 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:max-w-xs sm:text-sm sm:leading-6"
                />

              </div>
            </div>

            <Wide
              @label="Ausschankmenge"
              @name="serving_size"
              @type="text"
              @value={{displaySize @model.serving_size}}
              @update={{fn (mut @model.serving_size)}}
              @updateMut={{reverseDisplaySize}}
            />

          </div>
          <h2 class="text-base font-semibold leading-7 text-gray-900">Lager +
            Bestellungen</h2>
          {{! <p class="mt-1 text-sm leading-6 text-gray-600">Pfand und Preise</p> }}

          <div class="grid grid-cols-1 gap-x-6 sm:grid-cols-6">
            <div class="px-4 lg:px-8 col-span-6">
              <div class="mt-4 flow-root">
                <div class="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div
                    class="inline-block min-w-full py-2 align-middle px-4 lg:px-8"
                  >
                    <table class="min-w-full divide-y divide-gray-300">
                      <thead>
                        <tr>
                          <th
                            scope="col"
                            class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-3"
                          >Ort</th>
                          <th
                            scope="col"
                            class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >Standard (Gebinde | Einzel)</th>
                          <th
                            scope="col"
                            class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >Ist</th>
                          <th
                            scope="col"
                            class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                          >Fehlmenge</th>
                        </tr>
                      </thead>
                      <tbody class="bg-white">
                        {{#each
                          (sort @model.locationItems "location.name")
                          as |locationItem|
                        }}
                          <tr class="even:bg-gray-50">
                            <td
                              class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3"
                            >{{locationItem.location.name}}</td>
                            <td
                              class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                            >
                              <Input
                                @type="text"
                                @value={{locationItem.virtual_proposal_amount}}
                                name={{concat
                                  "proposal_amount-"
                                  locationItem.location.name
                                }}
                                id={{concat
                                  "proposal_amount-"
                                  locationItem.location.name
                                }}
                                class="w-12 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              />
                              <span
                                class="mx-1 text-3xl font-extralight"
                              ></span>
                              <Input
                                @type="text"
                                @value={{locationItem.virtual_proposal_amount_single}}
                                name={{concat
                                  "proposal_amount_single-"
                                  locationItem.location.name
                                }}
                                id={{concat
                                  "proposal_amount_single-"
                                  locationItem.location.name
                                }}
                                class="w-12 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              />

                            </td>
                            <td
                              class="whitespace-nowrap px-3 py-4 text-sm text-gray-500"
                            >{{locationItem.displayAmount}}</td>
                            <td
                              class="whitespace-nowrap px-3 py-4 text-sm text-gray-500
                                {{if
                                  (gt locationItem.diff 0)
                                  'text-red-500 font-bold'
                                  'text-green-500 '
                                }}"
                            >{{locationItem.diff}}</td>

                          </tr>

                        {{/each}}

                        {{#each @model.orderedAmountAndDate as |order|}}
                          <tr class="even:bg-gray-50">
                            <td
                              colspan="2"
                              class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3"
                            >
                              Lieferung zum
                              {{displayDate (get order 0)}}
                            </td>
                            <td
                              class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-3"
                            >
                              {{get order 1}}
                            </td>
                            <td>
                            </td>
                          </tr>
                        {{/each}}

                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <h2
            class="mt-8 text-base font-semibold leading-7 text-gray-900 mb-2"
          >Lieferanten
            <button
              {{on "click" this.addNewSupplyItem}}
              type="button"
              class="ml-4 rounded bg-indigo-600 px-2 py-1 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >hinzufügen</button></h2>
          {{#each
            (filterSort
              this.supplierItemsWithoutSpecial "!isDeleted" "supplier.name"
            )
            as |supItem idx|
          }}

            <div class="grid grid-cols-1 gap-x-6 sm:grid-cols-6">
              <h2 class="text-lg block col-span-6 ml-4 mt-2">
                Lieferant
                {{plusOne idx}}
                <button
                  {{on "click" (fn this.deleteSupplier supItem)}}
                  type="button"
                  class="ml-4 rounded bg-red-600 px-2 py-0.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-red-600"
                >löschen</button>
              </h2>
              <SelectSuppliers
                @label="Name"
                @name="supplier"
                @value={{supItem.supplier}}
                @onChange={{supItem.updateSupplier}}
                @hide_special={{true}}
              />
              <Wide
                @label="Artikelnummer"
                @name="number"
                @type="text"
                @value={{supItem.articlenr}}
              />
              <Wide
                @label="Preis"
                @name="price"
                @type="text"
                @value={{euro supItem.price}}
                @update={{fn (mut supItem.price)}}
                @updateMut={{reverseEuro}}
              />

              <Wide
                @label="Standardbestellung"
                @name="standardOrder"
                @type="text"
                @value={{supItem.standard_order}}
              />
            </div>
          {{else}}
            <h2 class="text-lg block col-span-6 ml-4 mt-2">
              Es wurden noch keine Lieferanten für diesen Artikel hinzugefügt.
            </h2>
          {{/each}}
        </div>
      </div>

      <div class="mt-6 flex items-center justify-end gap-x-6">
        <button
          {{on "click" this.cancel}}
          type="button"
          class="text-sm font-semibold leading-6 text-gray-900"
        >abbrechen</button>
        <button
          {{on "click" this.save}}
          type="button"
          class="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >Speichern</button>
      </div>
    </form>
  </template>
  @service store;
  @service notification;
  @action
  addNewSupplyItem() {
    const allSuppliers = this.store.peekAll('supplier') as SupplierModel[];
    if (allSuppliers.length === 0) {
      alert(
        'Es sind keine Lieferanten vorhanden. Bitte legen Sie zuerst einen Lieferanten an.'
      );
      return;
    }
    const supplier = allSuppliers.find((sup) => sup.additional !== 'special');
    if (!supplier) {
      alert(
        'Es sind keine gültigen Lieferanten vorhanden. Bitte legen Sie zuerst einen Lieferanten an.'
      );
      return;
    }

    this.store.createRecord<SupplierItemModel>('supplier-item', {
      supplier: supplier,
      item: this.args.model,
    });
  }
  @action
  deleteSupplier(supItem: SupplierItemModel) {
    supItem.deleteRecord();
    supItem.save().catch((e) => {
      console.error('Error deleting supplier item:', e);
      if (e.errors[0].status === '406') {
        this.notification.error(
          'Fehler beim Löschen des Lieferantenartikels. Der Lieferantenartikel wird noch verwendet.'
        );
      } else {
        this.notification.error(
          'Fehler beim Löschen des Lieferantenartikels. Bitte versuchen Sie es erneut.'
        );
      }
      supItem.rollback();
    });
  }
  @action updateContainerSize(val) {
    const model = this.args.model;
    const prev = model.virtual_target_amount;
    model.container_size = +val;
    model.target_amount = +val * prev;
  }
  @action
  cancel() {
    this.args.model.rollback();
    this.args.model.supplierItems.forEach((supItem: SupplierItemModel) => {
      supItem.rollback();
    });
    this.args.model.locationItems.forEach((locItem: LocationItemModel) => {
      locItem.rollbackAttributes();
    });
    this.args.closeModal();
  }
  @action
  async save() {
    try {
      const item = this.args.model;
      if (item.shouldBeSaved) {
        await item.save();
      }
      await this.saveLocationItems();
      await this.saveSupplierItems();
      this.args.closeModal();
    } catch (e) {
      console.error('Error saving item:', e);
      alert(
        'Fehler beim Speichern des Artikels. Bitte überprüfen Sie die Eingaben und versuchen Sie es erneut.'
      );
    }
  }
  @action
  async saveSupplierItems() {
    const promises: Promise<SupplierItemModel>[] = [];
    const supItems = this.args.model.supplierItems;
    supItems.forEach((item: SupplierItemModel) => {
      if (item.shouldBeSaved) {
        promises.push(item.save());
      }
    });
    await Promise.all(promises);
  }
  @action
  async saveLocationItems() {
    const promises: Promise<LocationItemModel>[] = [];
    const locItems = this.args.model.locationItems;
    locItems.forEach((item: LocationItemModel) => {
      if (item.shouldBeSaved) {
        promises.push(item.save());
      }
    });
    await Promise.all(promises);
  }
  get supplierItemsWithoutSpecial() {
    return this.args.model?.supplierItems.filter((item) => {
      return item.supplier.additional !== 'special';
    });
  }
}
